import { Button, Form, Modal, ModalBody, ModalFooter, ModalHeader, Col, Row } from 'reactstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import FormInput from '@/components/forms/FormInput';
import * as yup from 'yup';
import { addMinutes, getMinutes } from 'date-fns';
import { Environment, environment } from '@repo/environment';
import { yupResolver } from '@hookform/resolvers/yup';
import { TournamentRecurringType } from '@repo/common-types';
import useTournamentTemplates from '@/hooks/useTournamentTemplates';
import { createTournament } from '@/redux/slices/tournaments';
import { useFormStatusReducer } from '@/hooks/useFormStatusReducer';
import { useEffect } from 'react';
import { useGetEligibleTargetTournamentsQuery } from '@/redux/api/tournaments';

const tournamentFormSchema = yup.object({
  startTime: yup
    .date()
    .required('Start Time is required')
    .test('not-during-break', 'Start Time must not be during a break time (minute 55-59)', (value) => {
      const startTimeMinute = getMinutes(new Date(value));
      if (startTimeMinute >= 55 && startTimeMinute <= 59) {
        return false;
      }
      return true;
    }),
  templateId: yup.string().required('Template is required'),
  recurring: yup.boolean().optional(),
  recurringType: yup
    .string()
    .nullable()
    .when('recurring', {
      is: true,
      then: (schema) =>
        schema
          .required('Recurring Type is required when tournament is recurring')
          .test('not-empty', 'Recurring Type is required when tournament is recurring', (value) => {
            return value !== '' && value !== null && value !== undefined;
          }),
      otherwise: (schema) => schema.optional(),
    }),
  parentTournamentId: yup.string().optional(),
});

const TournamentModalForm = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch();
  const { tournamentTemplates } = useTournamentTemplates();
  const { data: tournaments } = useGetEligibleTargetTournamentsQuery();

  const [formStatus, dispatchFormStatus] = useFormStatusReducer();

  const eligibleTournaments = tournaments?.data;

  const shouldDefaultStartTime = [Environment.LOCAL, Environment.DEVELOPMENT, Environment.STAGING].includes(
    environment,
  );

  const methods = useForm({
    resolver: yupResolver(tournamentFormSchema),
    mode: 'onChange',
    defaultValues: shouldDefaultStartTime ? { startTime: addMinutes(new Date(), 3) } : undefined,
  });

  const {
    handleSubmit,
    formState: { isValid },
    control,
    watch,
    setValue,
  } = methods;

  const recurring = watch('recurring');

  useEffect(() => {
    if (!recurring) {
      setValue('recurringType', null);
    }
  }, [recurring, setValue]);

  const onSubmit = (data: yup.InferType<typeof tournamentFormSchema>) => {
    dispatch(
      createTournament({
        templateId: data.templateId,
        startTime: new Date(data.startTime).toISOString(),
        recurring: data.recurring,
        recurringType: data.recurringType,
        parentTournamentId: data.parentTournamentId,
        meta: {
          toast: {
            success: 'Tournament created successfully',
            error: 'Failed to create tournament',
          },
        },
      }),
    );
    onClose();
  };

  return (
    <Modal isOpen toggle={onClose} id="create-tournament-template" className="modal-lg" centered>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>Create New Tournament</ModalHeader>
          <ModalBody style={{ maxHeight: '75vh' }} className="overflow-auto">
            <div className="w-100 d-flex flex-column justify-content-start max-h-[75vh] gap-2 overflow-auto">
              <div className="w-100 d-flex justify-content-between flex-row gap-2">
                <Col>
                  <Row className="w-100">
                    <Col>
                      <FormInput control={control} label="Start Time" name="startTime" type="datetime-local" />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        label="Template"
                        name="templateId"
                        type="select"
                        options={tournamentTemplates?.map((template) => ({ value: template.id, label: template.name }))}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput control={control} label="Recurring" name="recurring" type="checkbox" />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        label="Target Tournament (satellite)"
                        name="parentTournamentId"
                        type="select"
                        disabled={recurring}
                        options={eligibleTournaments?.map((tournament) => ({
                          value: tournament.id,
                          label: tournament.name,
                        }))}
                      />
                    </Col>
                  </Row>
                  {recurring && (
                    <Row className="w-100">
                      <Col>
                        <FormInput
                          control={control}
                          label="Recurring Type"
                          name="recurringType"
                          type="select"
                          options={[
                            { value: '', label: '-- Select Recurring Type --' },
                            ...Object.values(TournamentRecurringType)
                              .filter((type) => type !== TournamentRecurringType.NONE)
                              .map((type) => ({
                                value: type,
                                label: type,
                              })),
                          ]}
                        />
                      </Col>
                      <Col />
                    </Row>
                  )}
                </Col>
              </div>
              {formStatus.error && (
                <div className="text-danger mt-2" role="alert">
                  {formStatus.error}
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="grey" onClick={onClose} disabled={formStatus.isLoading}>
              Cancel
            </Button>
            <Button disabled={!isValid || formStatus.isLoading} onClick={handleSubmit(onSubmit)} color="primary">
              {formStatus.isLoading ? 'Creating...' : 'Create New Tournament'}
            </Button>
          </ModalFooter>
        </Form>
      </FormProvider>
    </Modal>
  );
};

export default TournamentModalForm;
